import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  getSlackFiles,
  getSlackMembers,
  getSlackMessages,
  getSlackUserGroups,
  getSlackUsers,
} from 'features/agents/api/agentsApi';

const labelCls = 'block text-[12px] text-[color:var(--text-muted)]';

const SLACK_EMOJI = [
  { name: 'thumbsup', glyph: '👍' },
  { name: '+1', glyph: '👍' },
  { name: 'thumbsdown', glyph: '👎' },
  { name: '-1', glyph: '👎' },
  { name: 'heart', glyph: '❤️' },
  { name: 'eyes', glyph: '👀' },
  { name: 'tada', glyph: '🎉' },
  { name: 'white_check_mark', glyph: '✅' },
  { name: 'x', glyph: '❌' },
  { name: 'warning', glyph: '⚠️' },
  { name: 'fire', glyph: '🔥' },
  { name: 'rocket', glyph: '🚀' },
  { name: 'wave', glyph: '👋' },
  { name: 'clap', glyph: '👏' },
  { name: 'pray', glyph: '🙏' },
  { name: 'joy', glyph: '😂' },
  { name: 'thinking_face', glyph: '🤔' },
  { name: 'slightly_smiling_face', glyph: '🙂' },
  { name: 'disappointed', glyph: '😞' },
  { name: 'hourglass', glyph: '⌛' },
  { name: 'bell', glyph: '🔔' },
  { name: 'speech_balloon', glyph: '💬' },
  { name: 'link', glyph: '🔗' },
  { name: 'bulb', glyph: '💡' },
];

function isExpression(value) {
  return typeof value === 'string' && value.includes('{{');
}

function splitIds(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  return String(value).split(',').map((s) => s.trim()).filter(Boolean);
}

function joinIds(ids) {
  return ids.filter(Boolean).join(',');
}

function withCurrentOption(options, value, fallbackLabel) {
  if (!value || isExpression(value) || options.some((o) => o.value === value)) return options;
  return [{ value, label: fallbackLabel || 'Selected', hint: 'saved' }, ...options];
}

function ResourcePickerShell({
  field,
  value,
  onChange,
  options,
  loading,
  loadError,
  emptyTitle,
  emptyBody,
  placeholder,
  searchPlaceholder,
  onRefresh,
  help,
}) {
  const refreshLabel = loading ? 'Refreshing...' : 'Refresh';
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className={labelCls}>{field.label}</label>
        {onRefresh ? (
          <button
            type="button"
            onClick={() => onRefresh(true)}
            disabled={loading}
            className="inline-flex items-center gap-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {refreshLabel}
          </button>
        ) : null}
      </div>
      {field.help ? (
        <p className="text-[11px] leading-snug text-[color:var(--text-muted)] mb-1.5">{field.help}</p>
      ) : null}
      {loadError ? (
        <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5">
          <div className="text-[13px] font-medium text-[color:var(--text-primary)]">{loadError}</div>
          {onRefresh ? (
            <button type="button" onClick={() => onRefresh(true)} className="mt-2 text-[12px] font-medium text-[color:var(--text-primary)] underline">
              Retry
            </button>
          ) : null}
        </div>
      ) : !loading && options.length === 0 ? (
        <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5">
          <div className="text-[13px] font-medium text-[color:var(--text-primary)]">{emptyTitle}</div>
          <p className="text-[11px] text-[color:var(--text-muted)] mt-0.5 leading-snug">{emptyBody}</p>
        </div>
      ) : (
        <BrandDropdown
          triggerClassName="!h-8 !px-2 !py-0 !bg-transparent"
          value={value ?? ''}
          onChange={onChange}
          options={options}
          placeholder={loading ? 'Loading…' : placeholder}
          searchable
          searchPlaceholder={searchPlaceholder}
          emptyMessage="No matches."
          loading={loading}
          ariaLabel={field.label}
        />
      )}
      {help ? (
        <p className="text-[11px] leading-snug text-[color:var(--text-muted)] mt-2">{help}</p>
      ) : null}
    </div>
  );
}

function SlackUserPicker({ field, value, onChange, multiple, cfg }) {
  const channelField = field.channelField;
  const channelScoped = !!channelField;
  const channelId = channelScoped ? (cfg?.[channelField] || '') : '';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (channelScoped) {
      if (!channelId || isExpression(channelId)) {
        setUsers([]);
        setLoadError(false);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    setLoadError(false);
    try {
      const rows = channelScoped
        ? await getSlackMembers({ channel: channelId, refresh })
        : await getSlackUsers({ refresh });
      setUsers(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setUsers([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [channelScoped, channelId]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (!channelScoped || loading || loadError || isExpression(value) || !value) return;
    if (!users.some((u) => u.slack_id === value)) onChange('');
  }, [channelScoped, users, loading, loadError, value, onChange]);

  const options = useMemo(
    () => users.map((u) => ({
      value: u.slack_id,
      label: u.name || u.username || u.slack_id,
      hint: u.email || u.username || undefined,
    })),
    [users],
  );

  if (isExpression(value)) {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-1">Resolved from an upstream node at run time.</p>
      </div>
    );
  }

  if (channelScoped && !channelId) {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-1">Select a channel first.</p>
      </div>
    );
  }

  if (channelScoped && isExpression(channelId)) {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
          The channel is an expression, so the person is resolved when the Agent runs.
        </p>
      </div>
    );
  }

  const memberEmptyTitle = 'No channel members available';
  const memberEmptyBody = 'There are no members in this channel that the Trodo bot can list, or the bot is not in the channel.';
  const memberLoadError = 'Couldn\'t load channel members';

  if (multiple) {
    const selectedIds = splitIds(value);
    const selectedSet = new Set(selectedIds);
    const addOptions = options.filter((o) => !selectedSet.has(o.value));
    const remove = (id) => onChange(joinIds(selectedIds.filter((x) => x !== id)));
    const add = (id) => {
      if (!id || selectedSet.has(id)) return;
      onChange(joinIds([...selectedIds, id]));
    };
    return (
      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <label className={labelCls}>{field.label}</label>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {selectedIds.map((id) => {
              const opt = options.find((o) => o.value === id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => remove(id)}
                  className="inline-flex items-center gap-1 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[12px] text-[color:var(--text-primary)]"
                >
                  {opt?.label || id}
                  <X className="w-3 h-3 text-[color:var(--text-muted)]" />
                </button>
              );
            })}
          </div>
        )}
        <BrandDropdown
          triggerClassName="!h-8 !px-2 !py-0 !bg-transparent"
          value=""
          onChange={add}
          options={addOptions}
          placeholder={loading ? 'Loading…' : 'Add a person'}
          searchable
          searchPlaceholder="Search people..."
          emptyMessage="No people match your search."
          loading={loading}
          ariaLabel={field.label}
        />
      </div>
    );
  }

  return (
    <ResourcePickerShell
      field={field}
      value={value}
      onChange={onChange}
      options={withCurrentOption(options, value, 'Selected person')}
      loading={loading}
      loadError={loadError ? (channelScoped ? memberLoadError : 'Couldn\'t load Slack people') : null}
      emptyTitle={channelScoped ? memberEmptyTitle : 'No Slack people available'}
      emptyBody={channelScoped ? memberEmptyBody : 'Reconnect Slack if the workspace user list is empty.'}
      placeholder="Search people..."
      searchPlaceholder="Search people..."
      onRefresh={load}
    />
  );
}

function SlackMessagePicker({ field, value, onChange, cfg }) {
  const channelField = field.channelField || 'channel';
  const channelId = cfg?.[channelField] || '';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    if (!channelId || isExpression(channelId)) {
      setMessages([]);
      setLoading(false);
      setLoadError(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await getSlackMessages({ channel: channelId });
      setMessages(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setMessages([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  if (!channelId) {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-1">Select a channel or DM first.</p>
      </div>
    );
  }

  if (isExpression(channelId) || isExpression(value)) {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
          {isExpression(value)
            ? 'Resolved from an upstream node at run time.'
            : 'The channel is an expression, so the message is resolved when the Agent runs.'}
        </p>
      </div>
    );
  }

  const options = withCurrentOption(
    messages.map((m) => ({
      value: m.ts,
      label: m.text || '(no text)',
      hint: m.name || undefined,
    })),
    value,
    'Selected message',
  );

  return (
    <ResourcePickerShell
      field={field}
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      loadError={loadError ? 'Couldn\'t load recent messages' : null}
      emptyTitle="No recent messages"
      emptyBody="There are no recent messages in this conversation, or the Trodo bot cannot read them."
      placeholder="Select a message"
      searchPlaceholder="Search messages..."
      onRefresh={load}
    />
  );
}

function SlackFilePicker({ field, value, onChange, cfg }) {
  const channelId = cfg?.[field.channelField || 'channel'] || '';
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await getSlackFiles({ channel: isExpression(channelId) ? undefined : (channelId || undefined) });
      setFiles(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setFiles([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  const options = withCurrentOption(
    files.map((f) => ({
      value: f.slack_id,
      label: f.name || f.filename || f.slack_id,
      hint: f.filetype || undefined,
    })),
    value,
    'Selected file',
  );

  return (
    <ResourcePickerShell
      field={field}
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      loadError={loadError ? 'Couldn\'t load Slack files' : null}
      emptyTitle="No Slack files"
      emptyBody="No files are visible to the Trodo bot. Try a channel the bot can access."
      placeholder="Select a file"
      searchPlaceholder="Search files..."
      onRefresh={load}
    />
  );
}

function SlackUserGroupPicker({ field, value, onChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await getSlackUserGroups();
      setGroups(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setGroups([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const options = withCurrentOption(
    groups.map((g) => ({
      value: g.slack_id,
      label: g.name || g.handle || g.slack_id,
      hint: g.handle ? `@${g.handle}` : undefined,
    })),
    value,
    'Selected group',
  );

  return (
    <ResourcePickerShell
      field={field}
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      loadError={loadError ? 'Couldn\'t load user groups' : null}
      emptyTitle="No user groups"
      emptyBody="User groups require a paid Slack plan, and the Trodo bot needs usergroups:read."
      placeholder="Select a user group"
      searchPlaceholder="Search groups..."
      onRefresh={load}
    />
  );
}

function SlackEmojiPicker({ field, value, onChange }) {
  const options = SLACK_EMOJI.map((e) => ({
    value: e.name,
    label: `${e.glyph}  ${e.name.replace(/_/g, ' ')}`,
  }));
  return (
    <ResourcePickerShell
      field={field}
      value={value}
      onChange={onChange}
      options={withCurrentOption(options, value, value)}
      loading={false}
      loadError={null}
      emptyTitle="No emoji"
      emptyBody=""
      placeholder="Select a reaction"
      searchPlaceholder="Search emoji..."
    />
  );
}

export default function SlackResourcePicker({ field, value, onChange, cfg }) {
  switch (field.type) {
    case 'slack_user':
      return <SlackUserPicker field={field} value={value} onChange={onChange} cfg={cfg} />;
    case 'slack_users':
      return <SlackUserPicker field={field} value={value} onChange={onChange} multiple />;
    case 'slack_message':
      return <SlackMessagePicker field={field} value={value} onChange={onChange} cfg={cfg} />;
    case 'slack_file':
      return <SlackFilePicker field={field} value={value} onChange={onChange} cfg={cfg} />;
    case 'slack_usergroup':
      return <SlackUserGroupPicker field={field} value={value} onChange={onChange} />;
    case 'slack_emoji':
      return <SlackEmojiPicker field={field} value={value} onChange={onChange} />;
    default:
      return null;
  }
}
